import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { pluralize } from '@/lib/string-utils';
import { cn } from '@/lib/tailwind-utils';
import {
  formatShortDateWithDayOfWeek,
  formatTimeRange,
  getSubSessionsWithTimeChangeSummary,
  getSubTeacherLabel,
} from '@airreading/common';
import type {
  RecreateSessionsSummary,
  RecreateSessionSummaryItem,
  RecreateSubSessionTimeChangedSummary,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';

function SummaryContainer({
  sessions,
  title,
  showBorder,
  showSubTeacherDetails = false,
}: {
  sessions: RecreateSessionSummaryItem[];
  title: string;
  showBorder: boolean;
  showSubTeacherDetails?: boolean;
}) {
  return (
    sessions.length > 0 && (
      <div>
        <h4 className="text-muted-foreground mb-4">{title}:</h4>
        <div
          className={cn(
            'resize-none overflow-y-auto outline-none rounded-md p-2 flex flex-col gap-4',
            showBorder ? 'border h-32' : 'h-48',
          )}
        >
          {sessions.map((s) => {
            const start = Temporal.Instant.from(s.startInstant);
            const dateLabel = formatShortDateWithDayOfWeek(start, s.timeZone);
            const timeLabel = formatTimeRange(start, Temporal.Instant.from(s.endInstant), s.timeZone);
            return (
              <div
                key={`${s.startInstant}-${s.endInstant}-${s.timeZone}`}
                className="flex flex-row items-start gap-1 text-sm"
              >
                <span>•</span>
                <div className="flex flex-col gap-0.5">
                  <span>
                    {dateLabel}, {timeLabel}.
                  </span>
                  {showSubTeacherDetails ? (
                    <span>
                      Original teacher: {s.teacherName}, Sub teacher: {getSubTeacherLabel(s)}
                    </span>
                  ) : (
                    <span>Session teacher: {s.teacherName}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )
  );
}

function SubSessionsWithTimeChange({ sessions }: { sessions: RecreateSubSessionTimeChangedSummary[] }) {
  return (
    <div>
      <h4 className="text-muted-foreground mb-4">Sub sessions info:</h4>
      <div className="resize-none h-72 overflow-y-auto outline-none rounded-md p-2 flex flex-col gap-4">
        {sessions.map((s) => {
          const { originalTime, newTime } = getSubSessionsWithTimeChangeSummary(s);
          return (
            <div
              key={`${s.startInstant}-${s.endInstant}-${s.newStartInstant}-${s.newEndInstant}-${s.timeZone}`}
              className="flex flex-row items-start gap-1 text-sm"
            >
              <span>•</span>
              <div className="flex flex-col gap-0.5">
                <span>
                  Original time: {originalTime}, New time: {newTime}.
                </span>
                <span>
                  Original teacher: {s.teacherName}, Sub teacher: {getSubTeacherLabel(s)}.
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Single modal for Recreate Sessions.
 * - No class time change: shows canceled + sub sessions lists (normal text).
 * - Class time change: shows red text "X sub sessions time changed. Please notify teacher ops to follow up" and Sub sessions info with Original time / New time.
 */
export function RecreateSessionSummaryModal({
  open,
  onOpenChange,
  summary,
  onClose,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: RecreateSessionsSummary;
  onClose: () => void;
}) {
  const {
    classCount,
    canceledCount,
    subbedCount,
    canceledSessions,
    subSessions,
    subSessionsTimeChangedCount = 0,
    subSessionsTimeChanged = [],
  } = summary;
  const { toast } = useToast();
  const groupLabel = pluralize(classCount, 'student group', 'student groups');
  const canceledLabel = `${pluralize(canceledCount, 'session', 'sessions')} canceled`;
  const subbedLabel = `${pluralize(subbedCount, 'session', 'sessions')} subbed`;
  const hasTimeChange = subSessionsTimeChangedCount > 0 && subSessionsTimeChanged.length > 0;
  const timeChangedLabel = `${pluralize(subSessionsTimeChangedCount, 'sub session', 'sub sessions')} time changed`;
  const showBorder = canceledSessions.length > 0 && subSessions.length > 0;

  const onCopyClick = () => {
    const content = subSessionsTimeChanged
      .map((s) => {
        const { originalTime, newTime } = getSubSessionsWithTimeChangeSummary(s);
        return `Original time: ${originalTime}, New time: ${newTime}.\nOriginal teacher: ${s.teacherName}, Sub teacher: ${getSubTeacherLabel(s)}.\n`;
      })
      .join('\n');
    navigator.clipboard.writeText(content).then(() => {
      toast({
        title: 'Copied to clipboard',
      });
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" onCloseAutoFocus={onClose}>
        <DialogHeader>
          <DialogTitle>Recreate sessions summary</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground">
          Recreated sessions for {classCount} {groupLabel}. {canceledCount} {canceledLabel}.
          {hasTimeChange ? (
            <>
              {' '}
              <span className="text-destructive font-semibold">
                {subSessionsTimeChangedCount} {timeChangedLabel}. Please notify teacher ops to follow up.
              </span>
            </>
          ) : (
            <>
              {' '}
              {subbedCount} {subbedLabel}.
            </>
          )}
        </p>
        {hasTimeChange ? (
          <SubSessionsWithTimeChange sessions={subSessionsTimeChanged} />
        ) : (
          <>
            <SummaryContainer sessions={canceledSessions} title="Canceled sessions info" showBorder={showBorder} />
            <SummaryContainer
              sessions={subSessions}
              title="Sub sessions info"
              showBorder={showBorder}
              showSubTeacherDetails
            />
          </>
        )}
        <DialogFooter>
          {hasTimeChange ? (
            <Button type="button" onClick={onCopyClick}>
              Copy
            </Button>
          ) : (
            <Button type="button" onClick={onClose}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
